/* ===========================================================
   shared/seqdiff.js — which items two lists have in common

   Myers' O(ND) diff over lists of keys (strings). Returns the matched
   index pairs of a longest common subsequence. Common start and end
   are trimmed first, so the usual "edited a few paragraphs" case is
   near-instant even for long documents. A total rewrite (huge D)
   gives up on the middle and treats it as replaced, which is the
   honest answer anyway.
   =========================================================== */

const MAX_D = 1500;

/** @returns {Array<[number, number]>} pairs [i, j] with a[i] === b[j], increasing in both */
export function matchPairs(a, b) {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length, endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA--; endB--; }

  const pairs = [];
  for (let i = 0; i < start; i++) pairs.push([i, i]);
  const mid = myers(a.slice(start, endA), b.slice(start, endB));
  for (const [i, j] of mid) pairs.push([i + start, j + start]);
  for (let i = endA, j = endB; i < a.length; i++, j++) pairs.push([i, j]);
  return pairs;
}

function myers(a, b) {
  const n = a.length, m = b.length;
  if (!n || !m) return [];
  const max = Math.min(n + m, MAX_D);
  const off = max + 1;
  const v = new Int32Array(2 * max + 3);
  const trace = [];
  for (let d = 0; d <= max; d++) {
    trace.push(v.slice());
    for (let k = -d; k <= d; k += 2) {
      let x = (k === -d || (k !== d && v[k - 1 + off] < v[k + 1 + off])) ? v[k + 1 + off] : v[k - 1 + off] + 1;
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) { x++; y++; }
      v[k + off] = x;
      if (x >= n && y >= m) return backtrack(trace, n, m, off);
    }
  }
  return [];   // too different: no matches in the middle
}

function backtrack(trace, n, m, off) {
  const pairs = [];
  let x = n, y = m;
  for (let d = trace.length - 1; d >= 0; d--) {
    const v = trace[d];
    const k = x - y;
    const prevK = (k === -d || (k !== d && v[k - 1 + off] < v[k + 1 + off])) ? k + 1 : k - 1;
    const prevX = v[prevK + off];
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) { x--; y--; pairs.push([x, y]); }
    x = prevX; y = prevY;
  }
  return pairs.reverse();
}

/** Changed stretches of `side` relative to `base`, from the matched pairs. */
export function hunks(nBase, nSide, pairs) {
  const out = [];
  let bi = 0, si = 0;
  for (const [i, j] of pairs) {
    if (i > bi || j > si) out.push({ bStart: bi, bEnd: i, sStart: si, sEnd: j });
    bi = i + 1; si = j + 1;
  }
  if (bi < nBase || si < nSide) out.push({ bStart: bi, bEnd: nBase, sStart: si, sEnd: nSide });
  return out;
}
